import { Stack, useTheme } from "@mui/material";
import { Drawer } from "components";
import { memo, useCallback, useMemo, useRef, useState } from "react";
import PayNowCardV2, {
  PayNowCardV2Ref,
} from "../../create-subscription/PayNowCardV2";

interface ManageSeatsDrawerProps {
  open: boolean;
  onClose: () => void;
  subscriptionDetails:
    | {
        promotion_code?: string | null;
        seat_quantity?: number;
        percent_off?: number;
        price?: number;
        discount?: string | null;
        plan_id?: string | null;
        recurring_interval?: "month" | "year";
        pricing_type: "flat" | "tiered";
      }
    | null
    | undefined;
  billingPeriod?: "monthly" | "annually";
  price?: number;
  type: "EDIT" | "CREATE";
}

export const ManageSeatsDrawer = memo(
  ({
    open,
    onClose,
    billingPeriod,
    price,
    type,
    subscriptionDetails,
  }: ManageSeatsDrawerProps) => {
    const theme = useTheme();
    const payNowRef = useRef<PayNowCardV2Ref>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [childStatus, setChildStatus] = useState({
      isDisabled: true,
      isLoading: false,
    });

    const payNowData = useMemo(
      () => ({
        promocode: subscriptionDetails?.promotion_code ?? null,
        seatQuantity: subscriptionDetails?.seat_quantity ?? 0,
        discountPercentage:
          subscriptionDetails?.percent_off?.toString() ?? null,
        price: subscriptionDetails?.price,
        discountId: subscriptionDetails?.discount ?? null,
        planId: subscriptionDetails?.plan_id ?? null,
        recurringInterval: subscriptionDetails?.recurring_interval ?? "month",
        pricingType: subscriptionDetails?.pricing_type,
      }),
      [
        subscriptionDetails?.promotion_code,
        subscriptionDetails?.seat_quantity,
        subscriptionDetails?.percent_off,
        subscriptionDetails?.price,
        subscriptionDetails?.discount,
        subscriptionDetails?.plan_id,
        subscriptionDetails?.recurring_interval,
      ],
    );

    const handlePrimaryClick = useCallback(async () => {
      if (payNowRef.current) {
        setIsLoading(true);
        try {
          await payNowRef.current.handleProceed();
        } finally {
          setIsLoading(false);
        }
      }
    }, []);

    if (!open) return null;

    return (
      <Drawer
        open={open}
        title="Manage seats"
        onClose={onClose}
        primaryButtonText={type === "EDIT" ? "Update subscription" : "Proceed"}
        onPrimaryButtonClick={handlePrimaryClick}
        isPrimaryButtonDisabled={childStatus.isDisabled}
        isLoading={isLoading || childStatus.isLoading}
        secondaryButtonText="Cancel"
        onSecondaryButtonClick={onClose}
      >
        <Stack
          sx={{
            height: "100%",
            flex: 1,
            width: "614px",
            p: "8px",
            backgroundColor: theme.palette.neutral["005"],
          }}
        >
          <PayNowCardV2
            ref={payNowRef}
            type={type}
            data={payNowData}
            onClose={onClose}
            hideActionButton
            billingPeriod={billingPeriod}
            price={price}
            onStatusChange={setChildStatus}
          />
        </Stack>
      </Drawer>
    );
  },
);

ManageSeatsDrawer.displayName = "ManageSeatsDrawer";
